import { TBookingContextDef } from '@providers/booking/types'
import { isEmpty } from 'components-ui/src/shared/common'
import { TPetOffers, TProvidersAvailability, TProvidersAvailabilityLookup } from './types'

export const getPetOffers = (bookingCtx: TBookingContextDef): TPetOffers[] => {
  return bookingCtx.state.cart.groups.reduce((acc, group, groupIdx) => {
    const data = group.pets.reduce((accPet, pet, petIdx) => {
      if ((group.grooms[petIdx]?.offers?.length ?? 0) === 0) {
        return accPet
      }
      accPet.push({
        pet: {
          id: pet.id,
          name: pet.name,
          weight: pet.weight,
          specieId: group.specieId,
          breedId: pet.breedId,
          petIdx,
        },
        grooms: group.grooms[petIdx].offers.map((g) => ({
          id: g.offer.id || '',
          name: g.offer.displayName || '',
          baseName: g.offer.name || '',
          availabilities: g.availabilityGroups.availabilities || [],
        })),
        addons: (group.groomAddOn?.[petIdx] || []).map(({ offer, availability }) => ({ offer, availability })),
        groupIdx,
        state: group.grooms[petIdx].state,
        resource: group.grooms[petIdx].resource,
        requestDates: group.grooms[petIdx].requestDates || [],
      })
      return accPet
    }, [] as TPetOffers[])

    acc.push(...data)
    return acc
  }, [] as TPetOffers[])
}

export const getAvailableProviders = (groomOffer: TPetOffers) => {
  // First resolve which providers are available for the selected grooms
  // If there are more that one groom selected, sum the required minutes for the same provider
  const availability = groomOffer.grooms.reduce((acc, groom) => {
    groom.availabilities?.[0]?.providers?.forEach((provider) => {
      const providerKey = provider.id || ''
      if (acc[providerKey]) {
        acc[providerKey].requiredMinutes += provider.requiredMinutes || 0
      } else {
        acc[providerKey] = {
          id: provider.id || '',
          available: isEmpty(provider.available) ? false : provider.available!,
          displayName: provider.displayName || '',
          requiredMinutes: provider.requiredMinutes ?? 0,
          remainingMinutes: provider.remainingMinutes ?? 0,
        }
      }
    })
    return acc
  }, {} as TProvidersAvailabilityLookup)

  // Then resolve the minutes required for any addon selected for the same provider
  groomOffer.addons.forEach((addon) => {
    addon.availability.availabilities?.[0].providers?.forEach((provider) => {
      const prov = availability[provider.id || '']
      // if the provider is already in the list, add the required minutes for the addon
      // if is not there, no need to create it. The main availability will be used
      // from the providers found on the PrimaryService type (grooms)
      if (prov) {
        prov.requiredMinutes += provider.requiredMinutes || 0
      }
    })
  })

  return availability
}

export const getSortedGroomers = (groomers: TProvidersAvailabilityLookup) => {
  return Object.values(groomers).sort((a, b) => {
    if (a.available && !b.available) {
      return -1
    }
    if (!a.available && b.available) {
      return 1
    }
    return a.displayName.localeCompare(b.displayName)
  })
}

export const ANY_GROOMER_ID = 'anyGroomer'

export const anyGroomer = {
  id: ANY_GROOMER_ID,
  available: true,
  displayName: 'Any Groomer',
  requiredMinutes: 0,
  remainingMinutes: 0,
} as TProvidersAvailability
